/**
 * Pure helpers for the PayU checkout trigger. Factored out so they can be
 * unit-tested without a DOM / React render pass.
 */

export interface PayUCheckoutData {
  gateway: 'payu';
  action: string;
  params: Record<string, string | number | null | undefined>;
}

/**
 * Build a self-submitting HTML form that POST-redirects the browser to PayU's
 * hosted checkout. Returns a full `<html>…</html>` document string suitable
 * for `document.write` into a new document (or for embedding in an iframe).
 *
 * We escape attribute values because they flow from user-controlled fields
 * (firstname / email / productinfo) — unescaped `"` or `<` would break the
 * form or enable XSS. The `hash` field is hex-only so it's XSS-safe, but we
 * escape uniformly for simplicity.
 */
export function buildPayUFormHtml(data: PayUCheckoutData): string {
  const inputs = Object.entries(data.params)
    .filter(([, v]) => v !== null && v !== undefined)
    .map(([k, v]) => {
      const safeKey = escapeAttr(String(k));
      const safeValue = escapeAttr(String(v));
      return `<input type="hidden" name="${safeKey}" value="${safeValue}" />`;
    })
    .join('\n    ');

  return `<!doctype html>
<html>
<head><meta charset="utf-8"><title>Redirecting to PayU…</title></head>
<body style="font-family:system-ui,-apple-system,sans-serif;text-align:center;padding:80px 20px;color:#475569">
  <p style="font-size:14px">Redirecting to PayU, please wait…</p>
  <form id="payu-form" method="POST" action="${escapeAttr(data.action)}">
    ${inputs}
  </form>
  <script>document.getElementById('payu-form').submit();</script>
</body>
</html>`;
}

/**
 * Build a DOM form element (alternative to `buildPayUFormHtml`) that can be
 * appended to the current document and submitted. Useful for same-window
 * POST-redirect without opening a new document. Returns the form element;
 * caller appends + submits.
 */
export function buildPayUFormElement(
  data: PayUCheckoutData,
  doc: Document = document
): HTMLFormElement {
  const form = doc.createElement('form');
  form.method = 'POST';
  form.action = data.action;
  form.style.display = 'none';

  for (const [k, v] of Object.entries(data.params)) {
    if (v === null || v === undefined) continue;
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = k;
    input.value = String(v);
    form.appendChild(input);
  }
  return form;
}

export type ReturnStatus = 'success' | 'failure' | 'pending' | 'unknown';

/**
 * Classify the `status` query-param PayU appends to the return URL.
 * Never trust this for authoritative state — the webhook is the source of truth.
 */
export function classifyReturnStatus(status: string | null | undefined): ReturnStatus {
  if (!status) return 'unknown';
  const s = String(status).toLowerCase();
  if (['success', 'captured'].includes(s)) return 'success';
  if (['failure', 'failed', 'dropped', 'bounced', 'usercancelled', 'cancelled'].includes(s))
    return 'failure';
  if (['pending', 'initiated', 'in progress'].includes(s)) return 'pending';
  return 'unknown';
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
